package co.maytes.standupbot.dashboard.features.analytics

import androidx.compose.runtime.Composable
import co.maytes.standupbot.dashboard.components.widgets.cssRgb
import co.maytes.standupbot.dashboard.styles.Motion
import co.maytes.standupbot.dashboard.styles.Palette
import co.maytes.standupbot.dashboard.styles.Radius
import co.maytes.standupbot.dashboard.styles.Shadow
import co.maytes.standupbot.dashboard.styles.Spacing
import co.maytes.standupbot.dashboard.styles.Typography
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.px

data class TooltipContent(
    val title: String,
    val subtitle: String? = null,
    val rows: List<Row>,
) {
    data class Row(val label: String, val value: String, val color: String)
}

@Composable
fun ChartTooltip(
    content: TooltipContent?,
    x: Int,
    y: Int,
    visible: Boolean,
) {
    val displayed = content ?: return
    Box(
        modifier = Modifier
            .padding(leftRight = Spacing.sm, topBottom = Spacing.xs)
            .backgroundColor(Palette.surface)
            .borderRadius(Radius.lg)
            .styleModifier {
                property("position", "absolute")
                property("left", "${x}px")
                property("top", "${y}px")
                val lift = if (visible) "0px" else "4px"
                property("transform", "translate(-50%, calc(-100% - 14px + $lift))")
                property("pointer-events", "none")
                property("opacity", if (visible) "1" else "0")
                property("border", "1px solid ${Palette.border.cssRgb()}")
                property("box-shadow", Shadow.XL)
                property(
                    "transition",
                    "opacity ${Motion.DUR_BASE} ${Motion.EASE_STANDARD}, " +
                        "transform ${Motion.DUR_BASE} ${Motion.EASE_STANDARD}, " +
                        "left ${Motion.DUR_FAST} ${Motion.EASE_STANDARD}, " +
                        "top ${Motion.DUR_FAST} ${Motion.EASE_STANDARD}",
                )
                property("white-space", "nowrap")
                property("min-width", "152px")
                property("z-index", "10")
            },
    ) {
        Column(modifier = Modifier.gap(Spacing.xs)) {
            Column(modifier = Modifier.gap(2.px)) {
                SpanText(
                    text = displayed.title,
                    modifier = Modifier
                        .fontSize(Typography.bodySmall.size)
                        .fontWeight(700)
                        .color(Palette.textPrimary)
                        .styleModifier { property("letter-spacing", "-0.01em") },
                )
                displayed.subtitle?.let {
                    SpanText(
                        text = it,
                        modifier = Modifier
                            .fontSize(Typography.caption.size)
                            .color(Palette.textTertiary),
                    )
                }
            }
            Box(
                modifier = Modifier
                    .height(1.px)
                    .styleModifier {
                        property("width", "100%")
                        property("background", Palette.borderSubtle.cssRgb())
                    },
            )
            Column(modifier = Modifier.gap(Spacing.xxs)) {
                displayed.rows.forEach { row ->
                    Row(
                        verticalAlignment = Alignment.CenterVertically,
                        modifier = Modifier
                            .gap(Spacing.sm)
                            .styleModifier { property("width", "100%") },
                    ) {
                        Box(
                            modifier = Modifier
                                .width(9.px)
                                .height(9.px)
                                .borderRadius(3.px)
                                .styleModifier {
                                    property("background", row.color)
                                    property("box-shadow", "0 0 0 3px ${swatchHalo(row.color)}")
                                },
                        )
                        SpanText(
                            text = row.label,
                            modifier = Modifier
                                .fontSize(Typography.bodySmall.size)
                                .color(Palette.textSecondary)
                                .styleModifier {
                                    property("margin-right", "auto")
                                    property("padding-right", "16px")
                                },
                        )
                        SpanText(
                            text = row.value,
                            modifier = Modifier
                                .fontSize(Typography.bodySmall.size)
                                .fontWeight(700)
                                .color(Palette.textPrimary)
                                .styleModifier { property("font-variant-numeric", "tabular-nums") },
                        )
                    }
                }
            }
        }
    }
}

private fun swatchHalo(color: String): String {
    val clean = color.trim()
    if (!clean.startsWith("rgb(")) return "transparent"
    val inner = clean.removePrefix("rgb(").removeSuffix(")").trim()
    return "rgba($inner, 0.16)"
}
